import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { useLanguage } from '@/hooks/useLanguage';
import { supportedLanguages } from '@/i18n';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { User, Globe, LogOut } from 'lucide-react';
import { NamespaceLoader } from '@/components/NamespaceLoader';
import { Skeleton } from '@/components/ui/skeleton';

interface ProfileDropdownProps {
  className?: string;
}

/** Dropdown menu content – only rendered after navigation namespace is ready */
function ProfileDropdownItems({ onSignOut }: { onSignOut: () => void }) {
  const { t } = useTranslation(['navigation']);
  const { language, setLanguage } = useLanguage();

  return (
    <DropdownMenuContent align="end">
      <DropdownMenuItem asChild>
        <Link to="/profile" className="flex items-center gap-2">
          <User className="h-4 w-4" />
          {t('profile')}
        </Link>
      </DropdownMenuItem>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger className="flex items-center gap-2">
          <Globe className="h-4 w-4" />
          {supportedLanguages.find(l => l.code === language)?.flag} {t('language')}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          {supportedLanguages.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              className={language === lang.code ? 'bg-accent' : ''}
            >
              {lang.flag} {lang.nativeLabel}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={onSignOut} className="flex items-center gap-2 text-destructive">
        <LogOut className="h-4 w-4" />
        {t('signOut')}
      </DropdownMenuItem>
    </DropdownMenuContent>
  );
}

export function ProfileDropdown({ className }: ProfileDropdownProps) {
  const { user, account, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  if (!user) {
    return (
      <Link to="/auth">
        <NamespaceLoader namespaces={['navigation']} skeleton={<Skeleton className="h-8 w-16 rounded-md" />}>
          <SignInButton />
        </NamespaceLoader>
      </Link>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className={`gap-2 ${className || ''}`}>
          <User className="h-4 w-4" />
          <span className="hidden sm:inline">
            {account?.first_name || account?.email || 'User'}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <NamespaceLoader
        namespaces={['navigation']}
        skeleton={
          <DropdownMenuContent align="end">
            <div className="p-1 space-y-1">
              <Skeleton className="h-8 w-full rounded-sm" />
              <Skeleton className="h-8 w-full rounded-sm" />
              <Skeleton className="h-8 w-full rounded-sm" />
            </div>
          </DropdownMenuContent>
        }
      >
        <ProfileDropdownItems onSignOut={handleSignOut} />
      </NamespaceLoader>
    </DropdownMenu>
  );
}

/** Small component so t('signIn') is only called after namespace is ready */
function SignInButton() {
  const { t } = useTranslation(['navigation']);
  return <Button size="sm">{t('signIn')}</Button>;
}
